import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

const MarketingCapabilities = ({ agentId, showNotification }) => {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchCapabilities();
  }, [agentId]);

  const fetchCapabilities = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/ai/agents/${agentId}/capabilities/marketing`);
      if (response.data.success) {
        setCapabilities(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching marketing capabilities:', error);
      showNotification?.('Failed to load marketing capabilities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put(
        `/api/ai/agents/${agentId}/capabilities/marketing`,
        capabilities
      );
      if (response.data.success) {
        showNotification?.('Marketing capabilities saved successfully', 'success');
        setHasChanges(false);
      }
    } catch (error) {
      logger.error('Error saving marketing capabilities:', error);
      showNotification?.('Failed to save marketing capabilities', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    fetchCapabilities();
    setHasChanges(false);
  };

  const updateCapability = (path, value) => {
    setCapabilities(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!capabilities) {
    return <div className="p-4 text-gray-500">No marketing capabilities configured</div>;
  }

  return (
    <div className="space-y-6">
      {/* Master Enable Toggle */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={capabilities.enabled || false}
            onChange={(e) => updateCapability('enabled', e.target.checked)}
            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="ml-3 font-medium text-gray-900 dark:text-white">
            Enable Marketing Capabilities
          </span>
        </label>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Enable this agent to handle marketing tasks including campaign management, content approval, lead nurturing, and analytics.
        </p>
      </div>

      {capabilities.enabled && (
        <>
          {/* Campaign Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Campaign Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.campaignManagement?.enabled || false}
                  onChange={(e) => updateCapability('campaignManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable campaign management</span>
              </label>

              {capabilities.campaignManagement?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.campaignManagement?.autoSchedule || false}
                      onChange={(e) => updateCapability('campaignManagement.autoSchedule', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-schedule campaigns</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.campaignManagement?.approvalWorkflow || false}
                      onChange={(e) => updateCapability('campaignManagement.approvalWorkflow', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Require campaign approval</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.campaignManagement?.abTesting || false}
                      onChange={(e) => updateCapability('campaignManagement.abTesting', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enable A/B testing</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Content Approval */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Content Approval</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.contentApproval?.enabled || false}
                  onChange={(e) => updateCapability('contentApproval.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable content approval</span>
              </label>

              {capabilities.contentApproval?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.contentApproval?.brandGuidelines || false}
                      onChange={(e) => updateCapability('contentApproval.brandGuidelines', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Check brand guidelines</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.contentApproval?.complianceCheck || false}
                      onChange={(e) => updateCapability('contentApproval.complianceCheck', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Perform compliance checks</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Lead Nurturing */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Lead Nurturing</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.leadNurturing?.enabled || false}
                  onChange={(e) => updateCapability('leadNurturing.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable lead nurturing</span>
              </label>

              {capabilities.leadNurturing?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.leadNurturing?.autoSequences || false}
                      onChange={(e) => updateCapability('leadNurturing.autoSequences', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-trigger email sequences</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.leadNurturing?.scoringModel || false}
                      onChange={(e) => updateCapability('leadNurturing.scoringModel', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enable lead scoring model</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Analytics */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Analytics</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.analytics?.enabled || false}
                  onChange={(e) => updateCapability('analytics.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable analytics</span>
              </label>

              {capabilities.analytics?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.analytics?.trackingEnabled || false}
                      onChange={(e) => updateCapability('analytics.trackingEnabled', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enable campaign tracking</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.analytics?.reportGeneration || false}
                      onChange={(e) => updateCapability('analytics.reportGeneration', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-generate reports</span>
                  </label>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-[#1a1a1a] p-4 border-t border-gray-200 dark:border-gray-800 -mx-6 -mb-6">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MarketingCapabilities;
